import { NumberFormElementAnswerSchema, NumberFormElement as NumberFormElementType } from '@gearbelt/global';
import { NumericFormat } from 'react-number-format';
import { z } from 'zod';

import { Checkbox } from '@/components/ui/checkbox';
import { Input, inputVariants } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { ConfigLabel } from '../config-label';
import { FieldShell } from '../field-shell';

export const NumberFormElement = ({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  hideLabel,
  error,
}: {
  element: NumberFormElementType;
  value?: z.infer<typeof NumberFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof NumberFormElementAnswerSchema>['value']) => void;
  onBlur?: () => void;
  readonly?: boolean;
  hideLabel?: boolean;
  error?: string;
}) => {
  return (
    <FieldShell label={element.label} required={element.required} hideLabel={hideLabel} error={error}>
      <NumericFormat
        className={cn(
          inputVariants({
            error: false,
            hasPrefix: false,
            hasSuffix: false,
          }),
          'disabled:bg-grey-1',
        )}
        value={value ?? ''}
        onValueChange={(values) => {
          onChange(values.floatValue ?? null);
        }}
        onBlur={onBlur}
        placeholder={`Enter ${element.label}`}
        allowNegative={element.config?.allowNegative}
        thousandSeparator={element.config?.commaSeparator ? ',' : undefined}
        decimalScale={element.config?.allowDecimal ? undefined : 0}
        disabled={readonly}
        inputMode={element.config.allowNegative ? 'text' : element.config.allowDecimal ? 'decimal' : 'numeric'}
      />
    </FieldShell>
  );
};

export function NumberFormElementConfig({
  element,
  onChange,
}: {
  element: NumberFormElementType;
  onChange?: (updates: Partial<NumberFormElementType>) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox
          checked={element.config?.allowNegative}
          onCheckedChange={(checked) => onChange?.({ config: { ...element.config, allowNegative: !!checked } })}
        />
        <span>Allow negative numbers</span>
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox
          checked={element.config?.allowDecimal}
          onCheckedChange={(checked) => onChange?.({ config: { ...element.config, allowDecimal: !!checked } })}
        />
        <span>Allow decimal numbers</span>
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox
          checked={element.config?.commaSeparator}
          onCheckedChange={(checked) => onChange?.({ config: { ...element.config, commaSeparator: !!checked } })}
        />
        <span>Auto-insert comma separators (e.g. 1,000)</span>
      </ConfigLabel>
    </div>
  );
}
